"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  canUseMerchantPermission,
  type ShopShiftAssignmentRow,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { computeShiftWeekStats } from "@/modules/merchant/presentation/lib/shift-assignment-status";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopShiftMyPanels } from "@/modules/merchant/presentation/ui/shifts/ShopShiftMyPanels";
import { ShopShiftMySwapDialog } from "@/modules/merchant/presentation/ui/shifts/ShopShiftMySwapDialog";
import { ShopShiftMyToolbar } from "@/modules/merchant/presentation/ui/shifts/ShopShiftMyToolbar";
import { ShopShiftReportFilters } from "@/modules/merchant/presentation/ui/shifts/ShopShiftReportFilters";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useParams } from "@tanstack/react-router";
import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  startOfWeek,
} from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useCancelShopShiftSwapRequest,
  useClockInShopShiftAssignment,
  useClockOutShopShiftAssignment,
  useCreateShopShiftSwapRequest,
  useShopBranchesListQuery,
  useShopShiftAssignmentsListQuery,
  useShopShiftSwapRequestsListQuery,
} from "../api/queries";

export function ShopMyShiftsPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const [weekOffset, setWeekOffset] = useState(0);
  const [branchId, setBranchId] = useState<string | undefined>(undefined);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [scrollToDayKey, setScrollToDayKey] = useState<string | null>(null);

  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swapSourceId, setSwapSourceId] = useState("");
  const [swapTargetId, setSwapTargetId] = useState("");

  const branchesQuery = useShopBranchesListQuery(shopId);
  const branches = branchesQuery.data ?? [];

  useEffect(() => {
    if (branches.length > 0 && !branchId) {
      setBranchId(branches[0]?.id);
    }
  }, [branches, branchId]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(addWeeks(new Date(), weekOffset), {
      weekStartsOn: 1,
    });
    return eachDayOfInterval({
      start,
      end: endOfWeek(start, { weekStartsOn: 1 }),
    });
  }, [weekOffset]);

  const range = useMemo(() => {
    const from = format(weekDays[0] ?? new Date(), "yyyy-MM-dd");
    const to = format(weekDays[6] ?? new Date(), "yyyy-MM-dd");
    return { from, to };
  }, [weekDays]);

  const listQuery =
    branchId != null ? { branchId, from: range.from, to: range.to } : null;

  const assignments = useShopShiftAssignmentsListQuery(shopId, listQuery);
  const clockInMut = useClockInShopShiftAssignment(shopId);
  const clockOutMut = useClockOutShopShiftAssignment(shopId);
  const swapListQuery = useShopShiftSwapRequestsListQuery(
    shopId,
    branchId != null ? { branchId } : null,
  );
  const createSwapMut = useCreateShopShiftSwapRequest(shopId);
  const cancelSwapMut = useCancelShopShiftSwapRequest(shopId);

  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const canClock = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shifts.clock,
    verifiedLevel,
  );
  const canSwap = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shifts.clock,
    verifiedLevel,
  );
  const canViewReports = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shifts.manage,
    verifiedLevel,
  );

  const items = assignments.data?.items ?? [];
  const activeRows = useMemo(
    () => items.filter((a) => a.status !== "canceled"),
    [items],
  );

  const myStaffId = activeRows[0]?.shopStaffId;

  const sourceSwapOptions = useMemo(
    () => activeRows.filter((a) => !a.clockInAt),
    [activeRows],
  );

  const selectedSource = useMemo(
    () => items.find((a) => a.id === swapSourceId),
    [items, swapSourceId],
  );

  const targetSwapOptions = useMemo(() => {
    if (!selectedSource || myStaffId == null) return [];
    return items.filter(
      (a) =>
        a.status !== "canceled" &&
        !a.clockInAt &&
        a.shopStaffId !== myStaffId &&
        a.workDate === selectedSource.workDate &&
        a.branchId === selectedSource.branchId,
    );
  }, [items, selectedSource, myStaffId]);

  const swapRows = swapListQuery.data?.items ?? [];
  const pendingSwaps = swapRows.filter((r) => r.status === "pending").length;

  const byDate = useMemo(() => {
    const m = new Map<string, ShopShiftAssignmentRow[]>();
    for (const day of weekDays) {
      m.set(format(day, "yyyy-MM-dd"), []);
    }
    for (const a of activeRows) {
      const bucket = m.get(a.workDate);
      if (bucket) bucket.push(a);
    }
    for (const [, rows] of m) {
      rows.sort((x, y) => {
        const ax = x.clockInAt ?? x.scheduledStartAt;
        const bx = y.clockInAt ?? y.scheduledStartAt;
        return new Date(ax).getTime() - new Date(bx).getTime();
      });
    }
    return m;
  }, [activeRows, weekDays]);

  const countsByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const [key, rows] of byDate) {
      m.set(key, rows.length);
    }
    return m;
  }, [byDate]);

  const stats = useMemo(() => computeShiftWeekStats(activeRows), [activeRows]);

  const weekLabel = `${formatDateLocal(range.from)} – ${formatDateLocal(range.to)}`;
  const branchName = branches.find((b) => b.id === branchId)?.name ?? null;

  const handleSelectDay = useCallback((dateKey: string) => {
    setSelectedDayKey(dateKey);
    setScrollToDayKey(dateKey);
  }, []);

  useEffect(() => {
    setSelectedDayKey(null);
    setScrollToDayKey(null);
  }, [weekOffset, branchId]);

  const handleRefresh = useCallback(() => {
    void assignments.refetch();
    void swapListQuery.refetch();
  }, [assignments, swapListQuery]);

  const handleOpenSwapDialog = useCallback(() => {
    setSwapSourceId(sourceSwapOptions[0]?.id ?? "");
    setSwapTargetId("");
    setSwapDialogOpen(true);
  }, [sourceSwapOptions]);

  const loading =
    Boolean(branchId) &&
    (assignments.isLoading || swapListQuery.isLoading);

  return (
    <RequireMerchantPermission permission={MerchantPermissions.shifts.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.shiftMy(shopId)} />
      </Header>

      <Main>
        <ShopShiftMyToolbar
          shopId={shopId}
          branchName={branchName}
          weekLabel={weekLabel}
          stats={stats}
          pendingSwaps={pendingSwaps}
          canViewReports={canViewReports}
          isRefreshing={
            (assignments.isFetching && !assignments.isLoading) ||
            (swapListQuery.isFetching && !swapListQuery.isLoading)
          }
          onRefresh={handleRefresh}
        />

        <ShopShiftReportFilters
          branchId={branchId}
          branches={branches}
          onBranchChange={setBranchId}
          weekDays={weekDays}
          weekLabel={weekLabel}
          onWeekOffsetChange={setWeekOffset}
          countsByDate={countsByDate}
          selectedDayKey={selectedDayKey}
          onSelectDay={handleSelectDay}
        />

        {!branchId ? (
          <section className="rounded-xl border border-border bg-card px-6 py-12 text-center shadow-sm">
            <p className="text-muted-foreground text-sm">
              ເລືອກສາຂາເພື່ອເບິ່ງກະຂອງທ່ານ
            </p>
          </section>
        ) : (
          <ShopShiftMyPanels
            shopId={shopId}
            loading={loading}
            stats={stats}
            activeRows={activeRows}
            weekDays={weekDays}
            byDate={byDate}
            canClock={canClock}
            clockBusy={clockInMut.isPending || clockOutMut.isPending}
            onClockIn={(id) => void clockInMut.mutateAsync(id)}
            onClockOut={(id) => void clockOutMut.mutateAsync(id)}
            swapRows={swapRows}
            myStaffId={myStaffId}
            canSwap={canSwap}
            canCreateSwap={sourceSwapOptions.length > 0}
            swapCancelBusy={cancelSwapMut.isPending}
            onCancelSwap={(id) => void cancelSwapMut.mutateAsync(id)}
            onOpenSwapDialog={handleOpenSwapDialog}
            highlightDayKey={selectedDayKey}
            scrollToDayKey={scrollToDayKey}
            onScrolledToDay={() => setScrollToDayKey(null)}
          />
        )}

        <ShopShiftMySwapDialog
          open={swapDialogOpen}
          onOpenChange={setSwapDialogOpen}
          branchId={branchId}
          sourceOptions={sourceSwapOptions}
          targetOptions={targetSwapOptions}
          swapSourceId={swapSourceId}
          onSwapSourceChange={(id) => {
            setSwapSourceId(id);
            setSwapTargetId("");
          }}
          swapTargetId={swapTargetId}
          onSwapTargetChange={setSwapTargetId}
          submitting={createSwapMut.isPending}
          onSubmit={() => {
            if (!branchId || !swapSourceId || !swapTargetId) return;
            void createSwapMut
              .mutateAsync({
                branchId,
                sourceAssignmentId: swapSourceId,
                targetAssignmentId: swapTargetId,
              })
              .then(() => setSwapDialogOpen(false));
          }}
        />
      </Main>
    </RequireMerchantPermission>
  );
}
